import { BellRing, CandlestickChart, KeyRound, WalletCards, X, type LucideIcon } from 'lucide-react';
import { useEffect } from 'react';
import { Button } from '@/components/ui';
import { NAV_SECTIONS, routeInfo, type Route } from '@/lib/route';
import { cn } from '@/lib/utils';

const ICONS: Record<Route, LucideIcon> = {
  accounts: WalletCards,
  'credit-spreads': CandlestickChart,
  credentials: KeyRound,
  notifiers: BellRing,
};

/** The pages, in titled sections; the one showing is highlighted. */
function NavList({ current, onNavigate }: { current: Route; onNavigate?: () => void }) {
  return (
    <nav aria-label="Pages" className="flex flex-col gap-5">
      {NAV_SECTIONS.map((section) => (
        <div key={section.title} className="flex flex-col gap-0.5">
          <p className="px-2.5 pb-1 text-[10px] font-semibold tracking-wider text-muted uppercase">{section.title}</p>
          {section.routes.map((route) => {
            const info = routeInfo(route);
            const Icon = ICONS[route];
            const active = route === current;
            return (
              <a
                key={route}
                href={info.hash}
                onClick={onNavigate}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'flex h-8 items-center gap-2.5 rounded-md px-2.5 text-xs font-medium transition-colors',
                  'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
                  active ? 'bg-accent-soft text-accent' : 'text-ink-2 hover:bg-surface-2 hover:text-ink',
                )}
              >
                <Icon className="size-4 shrink-0" />
                {info.label}
              </a>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

/**
 * The navigation, as mini-cloud lays it out: a rail down the left from `lg` up, and below
 * that the same list in a drawer the top bar's menu button opens.
 */
export function Sidebar({ current, open, onClose }: { current: Route; open: boolean; onClose: () => void }) {
  // Escape closes the drawer, as it would a dialog.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <>
      <aside className="sticky top-12 hidden h-[calc(100dvh-3rem)] w-56 shrink-0 overflow-y-auto border-r border-line bg-surface px-2 py-4 lg:block">
        <NavList current={current} />
      </aside>

      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button type="button" aria-label="Close navigation" className="absolute inset-0 bg-black/50" onClick={onClose} />
          <aside className="absolute inset-y-0 left-0 flex w-64 max-w-[85vw] flex-col border-r border-line bg-surface shadow-xl">
            <div className="flex h-12 shrink-0 items-center justify-between border-b border-line px-3">
              <span className="text-sm font-bold tracking-tight">Fleece</span>
              <Button variant="ghost" onClick={onClose} aria-label="Close navigation">
                <X className="size-4" />
              </Button>
            </div>
            <div className="overflow-y-auto px-2 py-4">
              <NavList current={current} onNavigate={onClose} />
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
